/**
 * Pure selectors over `(DayStateMap, DaySlotMap)`.
 *
 * No React, no hooks — these are the functions the hook consumes to
 * derive UI-facing values. Keeping them outside React means we can
 * unit-test them without a renderer and reuse the same rules in a
 * future Preview panel.
 *
 * The single source of truth is:
 *   `state: DayStateMap`       — customs + droppedIds + autoFill flag
 *   `generated: DaySlotMap`    — fresh pipeline output
 *   day context (availability / past / has-interviewer)  — caller
 *
 * Every UI-facing enum is derived here; nothing lives in state
 * unless it's irreducible.
 */
import { mergePicks } from '../merge/mergePicks';
import type { DaySlotMap, SelfSlot } from '../types';
import type { DayStateMap } from './types';

export interface DayBookableInputs {
  dateKey: string;
  /** `dayRows` from `useSelfScheduleSlots`; only `hasAvailability` is read. */
  dayRows: Record<string, { hasAvailability: boolean }>;
  /**
   * Availability has resolved. It arrives on its own query, and until it does
   * `dayRows` is empty — every day would answer "unbookable".
   */
  availabilityKnown: boolean;
}

/**
 * Whether the calendar still offers this day. Gates the all-day grey wash, so
 * grey reads as "nothing bookable here" rather than "somebody is out"
 * (ENG-3567 review).
 *
 * Asks the pipeline's own output instead of counting who is out, because the
 * rule already differs by interview kind: `one_on_one` emits slots while ANY
 * interviewer is free, `panel` only while EVERY one is. A busy/free tally
 * would have to re-derive that split — and drift from it.
 */
export function isDayBookable({
  dateKey,
  dayRows,
  availabilityKnown,
}: DayBookableInputs): boolean {
  if (!availabilityKnown) return true;
  return Boolean(dayRows[dateKey]?.hasAvailability);
}

// ─────────────────────────────────────────────────────────────────
// Day-checkbox enum — one closed set replaces the legacy two-boolean
// (isChecked, isIndeterminate) pair.
// ─────────────────────────────────────────────────────────────────

/**
 *   `'empty'`    — unchecked, clickable.
 *   `'partial'`  — dash, clickable (click populates / cleans up).
 *   `'full'`     — tick, clickable (click clears).
 *   `'disabled'` — greyed out + tooltip; no picks possible.
 */
export type DayCheckbox = 'empty' | 'partial' | 'full' | 'disabled';

export interface DayCheckboxInputs {
  dateKey: string;
  state: DayStateMap;
  generated: DaySlotMap;
  hasAvailability: boolean;
  isPast: boolean;
  hasInterviewer: boolean;
}

export function dayCheckboxState({
  dateKey,
  state,
  generated,
  hasAvailability,
  isPast,
  hasInterviewer,
}: DayCheckboxInputs): DayCheckbox {
  if (!hasInterviewer) return 'disabled';
  if (isPast) return 'disabled';
  if (!hasAvailability) return 'disabled';

  const day = state[dateKey];
  if (!day) return 'empty';

  const hasActivePicks = mergePicks({
    customPicks: day.customPicks,
    generated: generated[dateKey] ?? [],
    autoFillRequested: day.autoFillRequested,
    droppedIds: day.droppedIds,
  }).length;
  if (hasActivePicks === 0) return 'empty';

  // "Full" = the day is actively auto-filled with no user-removed
  // slots. Custom picks on top of an auto-filled day do NOT
  // demote it — they're extras. This lets the week-checkbox reach
  // `full` even when individual days have customs.
  //
  //   autoFill=true  + droppedIds=empty → full   (any customs OK)
  //   autoFill=true  + droppedIds=some  → partial
  //   autoFill=false + customPicks>0    → partial
  //   autoFill=false + nothing          → empty (handled above)
  const pureAutoFill = day.autoFillRequested && day.droppedIds.size === 0;
  return pureAutoFill ? 'full' : 'partial';
}

// ─────────────────────────────────────────────────────────────────
// Week-level rollup — "ALL days" checkbox state. Same enum as
// per-day checkbox so the UI can share one renderer.
// ─────────────────────────────────────────────────────────────────

export interface WeekSelectionInputs {
  state: DayStateMap;
  generated: DaySlotMap;
  hasInterviewer: boolean;
  /** Future visible days — past-day filter is the caller's job. */
  futureVisibleDateKeys: string[];
  /** Whether each visible day has generator output. */
  hasAvailabilityByDate: Record<string, boolean>;
}

export function weekSelectionState({
  state,
  generated,
  hasInterviewer,
  futureVisibleDateKeys,
  hasAvailabilityByDate,
}: WeekSelectionInputs): DayCheckbox {
  if (!hasInterviewer) return 'disabled';

  const availableKeys = futureVisibleDateKeys.filter(
    (k) => hasAvailabilityByDate[k]
  );
  if (availableKeys.length === 0) return 'disabled';

  let fullCount = 0;
  let anyPicked = false;
  for (const dateKey of availableKeys) {
    const dayState = dayCheckboxState({
      dateKey,
      state,
      generated,
      hasAvailability: true,
      isPast: false,
      hasInterviewer: true,
    });
    if (dayState === 'full') fullCount += 1;
    if (dayState === 'full' || dayState === 'partial') anyPicked = true;
  }
  if (fullCount === availableKeys.length) return 'full';
  return anyPicked ? 'partial' : 'empty';
}

// ─────────────────────────────────────────────────────────────────
// Flat active-slot list (what the calendar renders + what we emit).
// ─────────────────────────────────────────────────────────────────

export function buildActiveSlots(
  state: DayStateMap,
  generated: DaySlotMap,
  /**
   * SCH-43: optional cache of the last-known pipeline output, keyed by
   * dateKey. Read ONLY when `generated` has NO entry for a key (i.e. the
   * availability range has windowed that date out). A key present with
   * an empty array is "data fetched, no slots" and must NOT fall back —
   * that's a real all-busy day. `droppedIds` filters both the live and
   * the cached set so a removed slot stays removed across navigation.
   */
  lastKnownGenerated?: DaySlotMap
): SelfSlot[] {
  const out: SelfSlot[] = [];
  const keys = new Set<string>([
    ...Object.keys(state),
    ...Object.keys(generated),
  ]);
  for (const dateKey of keys) {
    const day = state[dateKey];
    const generatedForDay = Object.prototype.hasOwnProperty.call(
      generated,
      dateKey
    )
      ? generated[dateKey]
      : (lastKnownGenerated?.[dateKey] ?? []);
    out.push(
      ...mergePicks({
        customPicks: day?.customPicks ?? [],
        generated: generatedForDay ?? [],
        autoFillRequested: day?.autoFillRequested ?? false,
        droppedIds: day?.droppedIds ?? new Set(),
      })
    );
  }
  return out;
}
